/**
 * Build a per-tool preview snippet for a queue row.
 *
 * The Attention Queue shows tool-approval items in a list where each row
 * is collapsed by default. Without a preview, the operator has to click
 * into each row to know whether the agent's proposed action is correct.
 * That's a lot of clicks for a queue of 40+ items, most of which are
 * routine.
 *
 * This helper extracts a small, scannable preview from the AttentionItem's
 * details payload — tool-aware, so an email shows subject + body, a review
 * reply shows the original review excerpt + the proposed reply, and so on.
 *
 * Design constraints:
 * - Output is shape-stable across tools (a "headline" line + a "body"
 *   block), so the row layout doesn't change with tool type — only the
 *   text content does. This keeps row heights uniform between previewed
 *   rows.
 * - Returns `null` when there's nothing useful to surface, so the row
 *   can simply skip rendering the preview block rather than reserve
 *   empty space. The variance is then bounded to one of two row heights
 *   (base, or base + preview) — no single row can dominate.
 * - Preview content is plain text, suitable for a fade-out-truncated
 *   multi-line block. We don't render markdown or HTML — those belong
 *   to the detail panel.
 *
 * The tool name is extracted with the same fallback chain as
 * `buildSyntheticDetail.ts` (`details.tool_name` → nested in
 * `proposed_resolution` → from a `Tool: ` title prefix).
 */

import type { AttentionItem } from './types'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * A tool-aware preview block to render below the row title.
 *
 * - `rating`: numeric 0-5 star rating when the tool surfaces one (e.g. a
 *   review reply). The row renders this as filled/empty star icons rather
 *   than baking glyphs into a string, so it can match the app's review UI.
 * - `reviewerName`: display name of the person who authored the content
 *   being acted on (e.g. an App Store reviewer), when available.
 * - `headline`: short, single-line summary (e.g. an email subject). Rendered
 *   as a slightly emphasised line at the top of the preview block.
 * - `body`: 1-3 lines of plain text, the bulk of the preview. Rendered
 *   below the headline with a fade-out truncation mask.
 */
export interface RowPreview {
  rating: number | null
  reviewerName: string | null
  headline: string | null
  body: string | null
}

/**
 * Compute a preview for a queue row, or null if there's nothing
 * useful to surface for this tool.
 */
export function buildRowPreview(item: AttentionItem): RowPreview | null {
  const details = (item.details ?? {}) as Record<string, unknown>
  const toolName = extractToolName(item, details)
  const args = isPlainObject(details.args) ? (details.args as Record<string, unknown>) : {}

  switch (toolName) {
    case 'send_email':
      return buildSendEmailPreview(args)
    case 'post_review_reply':
      return buildPostReviewReplyPreview(details, args)
    default:
      return buildGenericPreview(details, args)
  }
}

// ---------------------------------------------------------------------------
// Per-tool builders
// ---------------------------------------------------------------------------

function buildSendEmailPreview(args: Record<string, unknown>): RowPreview | null {
  const subject = stringOrNull(args.subject)
  const body = stringOrNull(args.body_text)

  if (!subject && !body) return null

  return {
    rating: null,
    reviewerName: null,
    headline: subject,
    body: body ? collapseWhitespace(body) : null,
  }
}

function buildPostReviewReplyPreview(
  details: Record<string, unknown>,
  args: Record<string, unknown>,
): RowPreview | null {
  // The original review lives in two possible shapes:
  //   - Canonical (agent-sourced, Task 4065a): rating + body in `args`
  //     (`review_rating`, `review_body`).
  //   - Legacy (workflow-sourced): rating in `trigger_payload.review.rating`,
  //     body in `trigger_payload.plain_text_body`.
  // Read canonical first, fall back to legacy — mirrors buildSyntheticDetail.
  const tp = isPlainObject(details.trigger_payload)
    ? (details.trigger_payload as Record<string, unknown>)
    : {}
  const review = isPlainObject(tp.review) ? (tp.review as Record<string, unknown>) : {}
  const caseNotes = isPlainObject(tp.case_notes) ? (tp.case_notes as Record<string, unknown>) : {}

  const rating = firstNumber(args.review_rating, review.rating)
  const reviewText = stringOrNull(args.review_body) ?? stringOrNull(tp.plain_text_body)
  const replyText = stringOrNull(args.response_text)

  // Reviewer name is best-effort: the canonical agent args don't carry it
  // today, so we probe the most likely legacy locations and degrade to null
  // (the row then shows a generic "App Store reviewer" label).
  const reviewerName =
    stringOrNull(args.reviewer_nickname) ??
    stringOrNull(review.reviewer_nickname) ??
    stringOrNull(caseNotes.reviewer_nickname) ??
    stringOrNull(tp.from)

  if (!reviewText && !replyText && rating === null) return null

  // Body previews the original review text — that's the context the operator
  // wants at a glance. We fall back to the proposed reply only when the
  // review body is unavailable. Rendering both would double row height and
  // break the uniform-size invariant.
  const body = reviewText ?? replyText
  return {
    rating,
    reviewerName,
    headline: null,
    body: body ? collapseWhitespace(body) : null,
  }
}

/**
 * Generic best-effort preview for tools we don't have a bespoke
 * template for. Picks the first long-ish string field from args, which
 * is usually the human-meaningful payload (a message, a note, a
 * description). Returns null when nothing reasonable can be found —
 * the row then renders without a preview block, which is preferable to
 * surfacing a meaningless field name.
 */
function buildGenericPreview(
  details: Record<string, unknown>,
  args: Record<string, unknown>,
): RowPreview | null {
  // Pending messages from the agent are usually the most useful piece
  // of natural-language context when there's no tool-specific preview.
  const pending = stringOrNull(details.pending_message)
  if (pending) {
    return { rating: null, reviewerName: null, headline: null, body: collapseWhitespace(pending) }
  }

  // Otherwise look for a long string in args. We pick the longest
  // string so that "message" beats "tone" beats "channel".
  let best: { key: string; value: string } | null = null
  for (const [k, v] of Object.entries(args)) {
    if (typeof v !== 'string') continue
    if (v.length < 20) continue
    if (!best || v.length > best.value.length) {
      best = { key: k, value: v }
    }
  }
  if (!best) return null
  return {
    rating: null,
    reviewerName: null,
    headline: null,
    body: collapseWhitespace(best.value),
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractToolName(item: AttentionItem, details: Record<string, unknown>): string {
  if (typeof details.tool_name === 'string' && details.tool_name) return details.tool_name
  if (isPlainObject(details.proposed_resolution)) {
    const pr = details.proposed_resolution as Record<string, unknown>
    if (typeof pr.tool_name === 'string' && pr.tool_name) return pr.tool_name
  }
  if (item.title.startsWith('Tool: ')) return item.title.slice('Tool: '.length)
  return item.title || 'unknown_tool'
}

function stringOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const trimmed = v.trim()
  return trimmed.length > 0 ? trimmed : null
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

/**
 * Collapse all whitespace runs (including newlines) into single spaces.
 *
 * The preview block has a fixed line count and uses CSS line-clamping —
 * embedded newlines would let a short message take more vertical space
 * than a long one, breaking the "rows are roughly uniform" invariant.
 * Flattening to single spaces lets the renderer make a clean, consistent
 * decision about where to truncate.
 */
function collapseWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

/**
 * Return the first argument that is a finite number, else null.
 *
 * Used to read a rating that may live in the canonical args
 * (`review_rating`) or the legacy trigger payload (`review.rating`).
 */
function firstNumber(...values: unknown[]): number | null {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) return v
  }
  return null
}
